import { useEffect } from 'react';

import { useRouter } from 'next/router';

import apiHooks, { ICreateUseRequestInfiniteResponse } from '@apiHooks';
import { OFFER_FIELDS } from '@components/pages/offers/[url]/config';
import { OfferCompilationStatusTypes } from 'src/types.d';
import routes from '@config/routes.json';

import OfferDetailView from '@components/pages/offers/[url]/OfferDetailView';

import {
    IGetOfferDetailDataFormatted,
    IGetTrafficSourcesDataFormatted,
    IGetCouponsDataFormatted,
    IGetOffersCompilationsDataFormatted,
    IGetUserProfileDataFormatted,
} from '@api/types';
import useRefreshToken from '@hooks/useRefreshToken';

import { IOfferDetailPageProps } from './interfaces';

const {
    useOfferDetail,
    useTrafficSources,
    useCoupons,
    useOffersCompilations,
    useUserProfile,
} = apiHooks;

const OfferDetailController: React.FC<IOfferDetailPageProps> = ({ urlParams, fallback }) => {
    const router = useRouter();
    const isAuth = useRefreshToken();

    const {
        data: offerDetail = fallback,
        error: offerDetailError,
        isLoading: isOfferDetailLoading,
    } = useOfferDetail<IGetOfferDetailDataFormatted>({
        urlParams: {
            id: urlParams?.url,
        },
        params: {
            lang: urlParams?.locale,
            viewRules: 'role_user',
            fields: OFFER_FIELDS,
        },
        optionalAuth: true,
        condition: !!urlParams?.url,
        dependencies: [urlParams?.url, urlParams?.locale],
    });

    const { data: offersCompilationsData = [] } = useOffersCompilations({
        params: {
            viewRules: 'role_user',
            status: 'active',
            limit: 30,
            offset: 0,
        },
        dependencies: [urlParams?.locale],
    }) as ICreateUseRequestInfiniteResponse<IGetOffersCompilationsDataFormatted[]>;

    const { data: trafficSources, isLoading: isLoadingTrafficSorces } = useTrafficSources<
        IGetTrafficSourcesDataFormatted[]
    >({
        optionalAuth: true,
        dependencies: [urlParams?.locale],
    });

    const { data: coupons, isLoading: isLoadingCoupons, error: couponsErrors } = useCoupons<
        IGetCouponsDataFormatted[]
    >({
        params: {
            limit: 6,
            offset: 0,
            lang: urlParams?.locale,
            sort: '-dateFrom',
            offerId: offerDetail?.id,
        },
        optionalAuth: true,
        condition: !!offerDetail?.id,
        dependencies: [offerDetail?.id, urlParams?.locale],
    });

    const { data: userData } = useUserProfile<IGetUserProfileDataFormatted>({ condition: isAuth });

    /**
     * если оффер еще не загрузился, то подразумеваем, что он есть
     * если не использовать isOfferDetailLoading, то будет редирект
     */
    const hasOffer = isOfferDetailLoading || (offerDetail && offerDetail?.id !== undefined);

    // если запрос с оффером вернул пустой объект, то редиректим на страницу с всеми офферами
    useEffect(() => {
        if (
            !hasOffer ||
            offerDetailError ||
            offerDetail?.status === OfferCompilationStatusTypes.disabled
        ) {
            router.push(`/${urlParams?.locale}${routes.offers}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasOffer, offerDetailError, offerDetail?.status, urlParams?.locale]);

    // так как мы испольуем ssg, то нужно, чтобы в отредеренных страницах была валидная верстка
    if (!hasOffer || offerDetailError) {
        return null;
    }

    return (
        <OfferDetailView
            offerDetail={offerDetail}
            offerDetailError={offerDetailError}
            isOfferDetailLoading={isOfferDetailLoading}
            trafficSource={trafficSources}
            isLoadingTrafficSorces={isLoadingTrafficSorces}
            coupons={coupons}
            isLoadingCoupons={isLoadingCoupons}
            couponsErrors={couponsErrors}
            compilations={offersCompilationsData}
            user={userData}
        />
    );
};

export default OfferDetailController;
